import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight, ListFilter, Sparkles, UserSearch, Map, FileCheck, Scale,
    Microscope, Radar, Gauge, ShieldCheck, UserCheck, Copy, Check,
} from "lucide-react";
import Logo from "../images/Logo.png";
import NetworkCanvas from "./NetworkCanvas";
import { FUNDER_NAV } from "./funder/FunderSidebar";
import { useFeatureFlags } from "./FeatureFlagsContext";
import { isUserLoggedIn } from "../service/AuthService";
import { getCalls } from "../service/FunderPortalService";
import { FEATURES } from "../constants/Constants";

/**
 * Funder-facing overview of the platform ("42Degrees for Funders").
 *
 * Content is ported from docs/FunderLandingPage.html; presentation follows
 * the researcher landing page (Landing.js + the landing-* styles) so the two
 * public faces of the app read as one product. Every "open the portal" call to
 * action is an in-app route into the funder portal (/funder/*), and the portal
 * preview is fed from the same demo data the portal renders, so the two can't
 * drift apart.
 *
 * The route itself is ungated (a shared link must never land on Under
 * Construction), but the portal it points at is behind feature flags — so
 * every portal CTA checks the flags and falls back to the contact address
 * when an admin has switched the portal (or a page) off.
 */

const CONTACT_EMAIL = "heather.maughan@fortytwodegrees.org";
const CONTACT_HREF = `mailto:${CONTACT_EMAIL}`;
const [EMAIL_LOCAL, EMAIL_DOMAIN] = CONTACT_EMAIL.split("@");
const PORTAL_PATH = "/funder";

// Feature flag guarding each portal route, from the portal's own nav list.
const FLAG_BY_PATH = Object.fromEntries(FUNDER_NAV.map((item) => [item.path, item.flag]));

const pillars = [
    { icon: Microscope, title: "Interpretable Measurement", desc: "Graph-theoretic features, not opaque generative models." },
    { icon: Radar, title: "Out-of-Distribution Signal", desc: "Novelty benchmarked against your own funded history." },
    { icon: Gauge, title: "Uncertainty-Quantified", desc: "Every score comes with a confidence band and source provenance." },
    { icon: ShieldCheck, title: "Auto-Deletion", desc: "Proposals are not stored after scoring." },
    { icon: UserCheck, title: "Human-in-the-Loop", desc: "Signals inform your team; your team decides." },
];

const features = [
    {
        icon: ListFilter,
        title: "Intelligent Triage",
        desc: "Signal-driven first-pass screening ranks and pre-sorts proposals so your team focuses deep review on borderline and standout candidates — not on obvious rejects or obvious keeps.",
        portal: { links: [{ label: "Submissions", path: "/funder/submissions" }], note: "triage queue with per-proposal audit rationale" },
    },
    {
        icon: Sparkles,
        title: "Novelty Discovery",
        desc: "An out-of-distribution signal flags proposals structurally distant from your funded portfolio — surfacing work that gets buried by volume, or missed because it doesn't look like anything you've funded before.",
        portal: { links: [{ label: "Call Overview", path: "/funder" }], note: "alignment map and emerging themes" },
    },
    {
        icon: UserSearch,
        title: "Expert Reviewer Discovery",
        desc: "The knowledge graph proposes reviewer candidates matched to each shortlisted proposal — with citations, expertise transparency, and conflict-of-interest flags — for your team to invite, disqualify, or override.",
        portal: { links: [{ label: "Reviewer Management", path: "/funder/reviewer-management" }], note: "matched reviewer shortlists" },
    },
    {
        icon: Map,
        title: "Portfolio Cartography",
        desc: "An interactive knowledge map overlays current proposals on your funded history. Every node traces to a source. Emerging themes are surfaced automatically and click through to the underlying research.",
        portal: {
            links: [
                { label: "Portfolio Overview", path: "/funder/portfolio" },
                { label: "White Space", path: "/funder/white-space" },
                { label: "Preprint Radar", path: "/funder/preprints" },
            ],
        },
    },
    {
        icon: FileCheck,
        title: "Decision Provenance",
        desc: "Every hard call generates a signed, versioned audit trail — reviewer input, platform signal, and rationale — that trustees, boards, or the public can read.",
        portal: {
            links: [
                { label: "Committee", path: "/funder/committee" },
                { label: "Decisions & Feedback", path: "/funder/decisions" },
            ],
        },
    },
    {
        icon: Scale,
        title: "Cross-Specialty Confidence",
        desc: "Consistent, calibrated scores across research domains — with domain-appropriate uncertainty — so your team can evaluate proposals in fields outside their own expertise and know when to call in specialists.",
        portal: { text: "Threaded through every view · same scoring approach, every specialty" },
    },
];

const mechanisms = [
    {
        title: "Interpretable Structural Features",
        desc: "Centrality, bridging, community structure, and type-composition ratios — familiar graph-theoretic quantities that describe how a proposal sits in the field. Every feature is inspectable and traceable to the underlying graph.",
    },
    {
        title: "Learned Graph Representations",
        desc: "A graph neural network complements the hand-crafted features by capturing higher-order patterns of connectivity. Trained unsupervised on the full graph, so structurally similar nodes end up with similar embeddings.",
    },
    {
        title: "Ordinal, Uncertainty-Aware Scoring",
        desc: "Scores fall in Low, Medium, or High bands with a 1–99 point value and an explicit confidence band. Stability under data perturbations and probability calibration make uncertainty a first-class output.",
    },
];

const sizes = [
    {
        title: "Small Charities",
        desc: "Five to twenty proposals per round · one to three decision-makers",
        points: [
            "Reclaim leadership and advisor time from proposal triage",
            "Reach expert reviewers beyond your advisory board",
            "See your funded history as a map, not a spreadsheet",
            "Give your board something rigorous to read",
        ],
    },
    {
        title: "Mid-Sized Foundations",
        desc: "Twenty to a hundred proposals per round · officers with mixed specialties",
        points: [
            "Consistent signals across research domains",
            "Catch novelty that thin reviewer pools miss",
            "Discover emerging themes in your portfolio",
            "Document rationale trustees will accept",
        ],
    },
    {
        title: "Large Trusts & Endowments",
        desc: "Hundreds of proposals per round · formal panels, boards, audit obligations",
        points: [
            "Reduce administrative load at scale",
            "Surface out-of-distribution proposals for deep panel read",
            "Reach reviewers beyond your existing database",
            "Withstand external and public scrutiny",
        ],
    },
];

// Node palette for the knowledge-graph diagram, drawn from the app's own
// score/accent colors (FunderUI + the landing page) rather than the mockup's.
const GRAPH = {
    bg: "var(--surface-2)",
    edge: "var(--border-strong)",
    edgeFar: "var(--border)",
    publication: "#a8a29a",
    publicationStroke: "#8a847c",
    method: "var(--series-blue)",
    problem: "var(--series-amber)",
    impact: "var(--series-green)",
    focal: "#5ecfa0",
    focalStroke: "#1e3330",
    text: "var(--ink-600)",
};

function KnowledgeGraphDiagram() {
    return (
        <svg
            viewBox="0 0 500 450"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Diagram of a research knowledge graph with a focal proposal at the center connected to methods, problems, impacts, and other publications"
        >
            <rect width="500" height="410" fill={GRAPH.bg} rx="12" />

            {/* Edges: focal to inner ring */}
            <g stroke={GRAPH.edge} strokeWidth="1.5" strokeLinecap="round">
                <line x1="250" y1="200" x2="324" y2="158" />
                <line x1="250" y1="200" x2="250" y2="115" />
                <line x1="250" y1="200" x2="176" y2="158" />
                <line x1="250" y1="200" x2="176" y2="242" />
                <line x1="250" y1="200" x2="250" y2="285" />
                <line x1="250" y1="200" x2="324" y2="242" />
            </g>

            {/* Edges: inner ring to outer publications */}
            <g stroke={GRAPH.edgeFar} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="3,3">
                <line x1="324" y1="158" x2="400" y2="120" />
                <line x1="324" y1="158" x2="410" y2="220" />
                <line x1="250" y1="115" x2="100" y2="120" />
                <line x1="176" y1="158" x2="90" y2="220" />
                <line x1="176" y1="242" x2="145" y2="335" />
                <line x1="250" y1="285" x2="355" y2="335" />
                <line x1="324" y1="242" x2="410" y2="220" />
            </g>

            {/* Outer publications */}
            <g fill={GRAPH.publication} stroke={GRAPH.publicationStroke} strokeWidth="1">
                <circle cx="400" cy="120" r="6" />
                <circle cx="410" cy="220" r="6" />
                <circle cx="355" cy="335" r="6" />
                <circle cx="145" cy="335" r="6" />
                <circle cx="90" cy="220" r="6" />
                <circle cx="100" cy="120" r="6" />
            </g>

            {/* Methods (hexagons) */}
            <g fill={GRAPH.method}>
                <polygon points="337,158 330,147 317,147 311,158 317,169 330,169" />
                <polygon points="189,242 182,231 169,231 163,242 169,253 182,253" />
            </g>

            {/* Problems (diamonds) */}
            <g fill={GRAPH.problem}>
                <polygon points="250,102 263,115 250,128 237,115" />
                <polygon points="250,272 263,285 250,298 237,285" />
            </g>

            {/* Impacts (triangles) */}
            <g fill={GRAPH.impact}>
                <polygon points="176,145 165,165 187,165" />
                <polygon points="324,229 313,249 335,249" />
            </g>

            {/* Focal proposal (star) */}
            <polygon
                points="250,178 255,193 271,193 259,203 263,218 250,209 237,218 241,203 229,193 245,193"
                fill={GRAPH.focal}
                stroke={GRAPH.focalStroke}
                strokeWidth="1.5"
                strokeLinejoin="round"
            />

            {/* Legend */}
            <g fontSize="12" fill={GRAPH.text}>
                <polygon points="55,417 57,422 63,423 58,426 60,432 55,429 50,432 52,426 47,423 53,422" fill={GRAPH.focal} stroke={GRAPH.focalStroke} strokeWidth="1" strokeLinejoin="round" />
                <text x="70" y="429">Your proposal</text>

                <polygon points="166,425 163,420 157,420 154,425 157,430 163,430" fill={GRAPH.method} />
                <text x="174" y="429">Method</text>

                <polygon points="240,419 246,425 240,431 234,425" fill={GRAPH.problem} />
                <text x="254" y="429">Problem</text>

                <polygon points="320,419 315,428 325,428" fill={GRAPH.impact} />
                <text x="335" y="429">Impact</text>

                <circle cx="400" cy="425" r="5" fill={GRAPH.publication} stroke={GRAPH.publicationStroke} strokeWidth="1" />
                <text x="412" y="429">Publication</text>
            </g>
        </svg>
    );
}

// "In the portal: …" cross-links under a feature card. Pages an admin has
// switched off are dropped so the card never advertises an Under Construction
// screen; if none are left the line is omitted.
function PortalLinks({ portal, navigate, canOpen }) {
    if (portal.text) {
        return <div className="flp-feature-portal">{portal.text}</div>;
    }
    const links = portal.links.filter((l) => canOpen(l.path));
    if (links.length === 0) return null;
    return (
        <div className="flp-feature-portal">
            In the portal:{" "}
            {links.map((l, i) => (
                <React.Fragment key={l.path}>
                    {i > 0 && " · "}
                    <button className="flp-feature-portal-link" onClick={() => navigate(l.path)}>{l.label}</button>
                </React.Fragment>
            ))}
            {portal.note && <> · {portal.note}</>}
        </div>
    );
}

// Visible, copyable address beside each mailto CTA. A mailto link does nothing
// on a machine with no mail handler (common on locked-down work laptops), so
// the address itself must always be reachable. Same copy-and-confirm pattern
// as the admin "Copy Link" buttons — except the confirmation is a floating tag
// below the icon, out of the line's flow, so the centered/right-aligned text
// doesn't jump sideways when it appears.
function CopyEmail({ dark = false }) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;
        const t = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(t);
    }, [copied]);

    // Legacy path for browsers without the async Clipboard API (and for the
    // cases where it rejects, e.g. an unfocused window): copy via a transient
    // selection. Returns whether the browser reported success.
    const copyLegacy = () => {
        const ta = document.createElement("textarea");
        ta.value = CONTACT_EMAIL;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand("copy"); } catch { ok = false; }
        document.body.removeChild(ta);
        return ok;
    };

    const copy = () => {
        const done = () => setCopied(true);
        const fallback = () => { if (copyLegacy()) done(); };  // otherwise the address stays visible to retype
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(CONTACT_EMAIL).then(done).catch(fallback);
        } else {
            fallback();
        }
    };

    return (
        <p className={`flp-email-line${dark ? " flp-email-line-dark" : ""}`}>
            or email{" "}
            <button type="button" className="flp-email-copy" onClick={copy} title="Copy address">
                {/* preferred wrap point on narrow screens: after the "@", never mid-domain */}
                {EMAIL_LOCAL}@<wbr />{EMAIL_DOMAIN}
                <span className="flp-email-icon">
                    {copied
                        ? <Check size={13} className="flp-email-icon-ok" aria-hidden="true" />
                        : <Copy size={13} aria-hidden="true" />}
                    {copied && <span className="flp-email-copied" role="status">Copied</span>}
                </span>
            </button>
        </p>
    );
}

// Primary portal CTA: opens the portal when it is live, otherwise offers the
// contact address in the same slot so the page never dead-ends.
function PortalButton({ available, onOpen, className, children }) {
    if (available) {
        return <button className={className} onClick={onOpen}>{children}</button>;
    }
    return <a className={className} href={CONTACT_HREF}>Talk to Us</a>;
}

export default function FunderLanding() {
    const navigate = useNavigate();
    const { isEnabled } = useFeatureFlags();
    // Validity-aware, same as PublicLayout: a stale token must not flip the
    // nav into the logged-in state for a visitor arriving from a shared link.
    const loggedIn = isUserLoggedIn();

    // The portal preview mirrors what the portal actually opens on: the
    // active demo call and its real proposal count.
    const calls = getCalls();
    const demoCall = calls.find((c) => c.status === "active") || calls[0];

    // Only advertise portal pages a visitor can actually open right now.
    // "/funder" is itself gated by the Call Overview flag, so the primary CTA
    // enters through the first page that is live rather than dead-ending when
    // only the overview is switched off.
    const portalOn = isEnabled(FEATURES.FUNDER_PORTAL);
    const canOpen = (path) => portalOn && isEnabled(FLAG_BY_PATH[path]);
    const portalTabs = FUNDER_NAV.filter((item) => canOpen(item.path));
    const portalEntry = canOpen(PORTAL_PATH) ? PORTAL_PATH : portalTabs[0]?.path;
    const canOpenPortal = Boolean(portalEntry);

    // A page meant to be shared by link deserves its own tab title; restore
    // the app default when navigating away.
    useEffect(() => {
        const previous = document.title;
        document.title = "42Degrees for Funders — DeepThought";
        return () => { document.title = previous; };
    }, []);

    // React Router keeps the previous page's scroll offset, so arriving from
    // deep inside the portal would open this page mid-way. Start at the top
    // unless the link targets a section (#platform etc.).
    useEffect(() => {
        if (!window.location.hash) window.scrollTo(0, 0);
    }, []);

    const openPortal = () => navigate(portalEntry);
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    return (
        <div className="landing-page flp-page">
            {/* ─── Nav ──────────────────────────────────── */}
            <nav className="landing-nav">
                <div className="landing-nav-inner">
                    <div className="landing-nav-brand" onClick={scrollToTop}>
                        <img src={Logo} alt="DeepThought" className="landing-nav-logo theme-invert" />
                        <span className="landing-nav-name">DeepThought</span>
                        <span className="flp-nav-tag">for Funders</span>
                    </div>
                    <div className="landing-nav-links">
                        <a href="#platform" className="landing-nav-link">Platform</a>
                        <a href="#how" className="landing-nav-link">How It Works</a>
                        <a href="#portal" className="landing-nav-link">Live Demo</a>
                        <a href="#pilot" className="landing-nav-link">Pilot</a>
                    </div>
                    <div className="landing-nav-actions">
                        {loggedIn ? (
                            <button className="landing-nav-login" onClick={() => navigate("/")}>
                                {/* "Home" is what the app's own mobile nav calls the dashboard */}
                                <span className="flp-label-long">Dashboard</span>
                                <span className="flp-label-short">Home</span>
                            </button>
                        ) : (
                            <button className="landing-nav-login" onClick={() => navigate("/login")}>Log In</button>
                        )}
                        <PortalButton available={canOpenPortal} onOpen={openPortal} className="landing-nav-cta">
                            <span className="flp-label-long">Open the Portal</span>
                            <span className="flp-label-short">Portal</span>
                            <ArrowRight size={14} />
                        </PortalButton>
                    </div>
                </div>
            </nav>

            {/* ─── Hero ─────────────────────────────────── */}
            <section className="landing-hero" id="top">
                <NetworkCanvas />
                <div className="landing-hero-content flp-hero-content">
                    <div className="landing-hero-badge">Research Intelligence for Funders</div>
                    <h1 className="landing-hero-title flp-hero-title">
                        A knowledge map for <span className="flp-accent">the research you fund</span>.
                        Signals for every proposal you review.
                    </h1>
                    <p className="landing-hero-sub flp-hero-sub">
                        42Degrees is a platform built on an interpretable, field-specific knowledge graph. It scores
                        every incoming grant proposal on novelty, methodology, and impact — with confidence bands and
                        source provenance — so your team can triage, discover, and decide with more evidence and less
                        black-box guesswork.
                    </p>
                    <div className="landing-hero-actions">
                        {canOpenPortal ? (
                            <>
                                <button className="landing-hero-btn" onClick={openPortal}>
                                    Open the Portal <ArrowRight size={16} />
                                </button>
                                <a className="landing-hero-btn-outline" href="#platform">See What It Does</a>
                            </>
                        ) : (
                            <>
                                <a className="landing-hero-btn" href="#platform">
                                    See What It Does <ArrowRight size={16} />
                                </a>
                                <a className="landing-hero-btn-outline" href={CONTACT_HREF}>Talk to Us</a>
                            </>
                        )}
                    </div>
                    <p className="landing-hero-trust">
                        {canOpenPortal
                            ? <>Live demo on synthetic data &middot; No login required &middot; Signals, not decisions</>
                            : <>Interpretable scoring &middot; Human-in-the-loop &middot; Signals, not decisions</>}
                    </p>
                </div>
            </section>

            {/* ─── Five foundations ─────────────────────── */}
            <section className="flp-pillars">
                <div className="landing-section-inner">
                    <p className="landing-section-label flp-center">Built on five foundations</p>
                    <div className="flp-pillar-grid">
                        {pillars.map((p) => {
                            const Icon = p.icon;
                            return (
                                <div className="flp-pillar" key={p.title}>
                                    <div className="flp-pillar-icon"><Icon size={18} /></div>
                                    <div className="flp-pillar-title">{p.title}</div>
                                    <p className="flp-pillar-desc">{p.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ─── Platform ─────────────────────────────── */}
            <section className="landing-features" id="platform">
                <div className="landing-section-inner">
                    <p className="landing-section-label">The Platform</p>
                    <h2 className="landing-section-title">Six things 42Degrees does for research funders</h2>
                    <p className="landing-section-sub">
                        Every capability is grounded in the same knowledge graph and the same interpretable scoring
                        approach. Every capability leaves the final decision with your team.
                    </p>
                    <div className="landing-features-grid">
                        {features.map((f) => {
                            const Icon = f.icon;
                            return (
                                <div className="landing-feature-card flp-feature-card" key={f.title}>
                                    <div className="landing-feature-icon"><Icon size={22} /></div>
                                    <h3 className="landing-feature-title">{f.title}</h3>
                                    <p className="landing-feature-desc">{f.desc}</p>
                                    <PortalLinks portal={f.portal} navigate={navigate} canOpen={canOpen} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ─── Methodology ──────────────────────────── */}
            <section className="landing-how" id="how">
                <div className="landing-section-inner">
                    <p className="landing-section-label">The Methodology</p>
                    <h2 className="landing-section-title">A knowledge graph, not a black box</h2>
                    <p className="landing-section-sub">
                        Scores emerge from where an incoming proposal lands in a heterogeneous graph of the field's
                        published literature — an interpretable, source-anchored alternative to opaque text generation.
                    </p>

                    <div className="flp-graph-explain">
                        <div className="flp-graph-diagram">
                            <KnowledgeGraphDiagram />
                        </div>
                        <div className="flp-graph-text">
                            <h3>The Knowledge Graph</h3>
                            <p>
                                The graph is built from a field's published literature. Every publication, method,
                                problem, and impact becomes a node. Edges capture the real relationships — a
                                publication <strong>uses</strong> particular methods, <strong>addresses</strong> particular
                                problems, and <strong>produces</strong> particular impacts.
                            </p>
                            <p>
                                When you submit a proposal, 42Degrees embeds it into this landscape. Proposals that
                                address familiar questions with familiar methods land in similar neighborhoods.
                                Proposals that bridge disparate methods, communities, or problem–impact combinations
                                sit in unusual structural positions.
                            </p>
                            <p>
                                Where a proposal lands carries information about the nature of its contribution — long
                                before any grant is awarded or paper published.
                            </p>
                        </div>
                    </div>

                    <div className="flp-mechanisms">
                        {mechanisms.map((m) => (
                            <div className="flp-mechanism-card" key={m.title}>
                                <h4>{m.title}</h4>
                                <p>{m.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Portal preview ───────────────────────── */}
            <section className="landing-about" id="portal">
                <div className="landing-section-inner">
                    <div className="landing-about-card flp-portal-card">
                        <NetworkCanvas />
                        <div className="landing-about-content">
                            <p className="landing-section-label" style={{ color: "#5ecfa0" }}>See It in Action</p>
                            <h2 className="landing-about-title">Explore the 42Degrees Funder Portal</h2>
                            <p className="landing-about-text">
                                A working demonstration populated with synthetic data. The fastest way to see how the
                                platform fits into your program — before we ever look at a real proposal.
                            </p>
                        </div>

                        <div className="flp-window">
                            <div className="flp-window-bar">
                                <div className="flp-window-dots"><span /><span /><span /></div>
                                <span className="flp-window-url">{window.location.host}{PORTAL_PATH}</span>
                            </div>
                            <div className="flp-window-body">
                                <span className="flp-synth-tag">Synthetic data · illustrative demonstration</span>
                                <h3 className="flp-window-title">{demoCall.title}</h3>
                                <p className="flp-window-sub">
                                    Program-officer workspace with continuous signal analysis across triage,
                                    shortlist, expert review, and committee. {demoCall.totalSubmissions} synthetic
                                    proposals from a representative call.
                                </p>
                                <PortalButton available={canOpenPortal} onOpen={openPortal} className="landing-hero-btn">
                                    Open the Portal <ArrowRight size={16} />
                                </PortalButton>
                                {portalTabs.length > 0 && (
                                    <div className="flp-tabs">
                                        {portalTabs.map((tab) => (
                                            <button className="flp-tab" key={tab.path} onClick={() => navigate(tab.path)}>
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── Scale ────────────────────────────────── */}
            <section className="landing-how">
                <div className="landing-section-inner">
                    <p className="landing-section-label">Whatever Your Scale</p>
                    <h2 className="landing-section-title">From single-mission charities to multi-billion trusts</h2>
                    <p className="landing-section-sub">
                        Same platform. Different pressure points. The same interpretable scoring approach works when
                        you review five proposals a year or five hundred.
                    </p>
                    <div className="flp-sizes">
                        {sizes.map((s) => (
                            <div className="flp-size-card" key={s.title}>
                                <h3>{s.title}</h3>
                                <p className="flp-size-desc">{s.desc}</p>
                                <ul>
                                    {s.points.map((pt) => (
                                        <li key={pt}><ArrowRight size={13} />{pt}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Pilot ────────────────────────────────── */}
            <section className="landing-features flp-pilot-section" id="pilot">
                <div className="landing-section-inner">
                    <div className="flp-pilot">
                        <div>
                            <p className="landing-section-label">Start Where the Risk Is Lowest</p>
                            <h2 className="flp-pilot-title">Retrospective on a recent funding round</h2>
                            <p>
                                We blind-score a past round of proposals using 42Degrees, then run a joint workshop with
                                your team to compare against your actual funding decisions. Success criteria are agreed
                                up front.
                            </p>
                            <p className="flp-scale-note">
                                Scales from small charities (five to twenty proposals per round) to large trusts (two
                                hundred or more).
                            </p>
                        </div>
                        <div className="flp-pilot-action">
                            <a
                                className="landing-nav-cta flp-btn"
                                href={`${CONTACT_HREF}?subject=${encodeURIComponent("42Degrees Retrospective Pilot")}`}
                            >
                                Discuss a Retrospective <ArrowRight size={15} />
                            </a>
                            <CopyEmail />
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── CTA ──────────────────────────────────── */}
            <section className="landing-cta">
                <div className="landing-section-inner">
                    <div className="landing-cta-card">
                        <NetworkCanvas />
                        <div className="landing-cta-content">
                            <h2 className="landing-cta-title">Open the portal. Bring your questions.</h2>
                            <p className="landing-cta-sub">
                                The funder portal is populated with synthetic data as an illustrative demonstration — the
                                fastest way to see how 42Degrees fits into your program without touching a real proposal.
                            </p>
                            <div className="landing-hero-actions" style={{ marginBottom: 0 }}>
                                {canOpenPortal ? (
                                    <>
                                        <button className="landing-hero-btn" onClick={openPortal}>
                                            Open the Portal <ArrowRight size={16} />
                                        </button>
                                        <a className="landing-hero-btn-outline" href={CONTACT_HREF}>Talk to Us</a>
                                    </>
                                ) : (
                                    <a className="landing-hero-btn" href={CONTACT_HREF}>
                                        Talk to Us <ArrowRight size={16} />
                                    </a>
                                )}
                            </div>
                            <CopyEmail dark />
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── Footer ───────────────────────────────── */}
            <footer className="landing-footer">
                <div className="landing-footer-inner">
                    <div className="landing-footer-brand">
                        <img src={Logo} alt="DeepThought" className="landing-nav-logo" />
                        <span className="landing-nav-name">DeepThought</span>
                    </div>
                    <p className="landing-footer-copy">
                        &copy; {new Date().getFullYear()} 42Degrees. All rights reserved.
                    </p>
                    <div className="landing-footer-links">
                        <button className="landing-footer-link" onClick={() => navigate("/about")}>About Us</button>
                        <a href="/whitepaper.pdf" target="_blank" rel="noopener noreferrer" className="landing-footer-link">White Paper</a>
                        <button className="landing-footer-link" onClick={() => navigate("/faq")}>FAQ</button>
                        <button className="landing-footer-link" onClick={() => navigate("/terms")}>Terms</button>
                        <button className="landing-footer-link" onClick={() => navigate("/privacy")}>Privacy</button>
                    </div>
                </div>
            </footer>
        </div>
    );
}
